import { PageTexts } from './PageTexts';
import * as Icons from '@lucide/svelte';
import type { Component } from 'svelte';
import type { DropdownPointData } from '../components/dropdown/dropdown.types';
import type { NavPointData } from '../components/navigation/navigation.types';
import type { PointBadges } from '../components/points/point.types';
import type { IconType } from '../components/icons/icons.types';
import { HelpersIcons } from '../modules/helpers/HelpersIcons';
import type { PageAccess, PageDefinition, PageKey } from './page.types';
import { UserService } from './UserService.svelte';
import { AiIcons } from '../modules/ai/AiIcons';
import { EntitiesIcons } from '../modules/entities/EntitiesIcons';
import { IntranetIcons } from '../modules/intranet/IntranetIcons';
import { DatabaseIcons } from '../modules/root/DatabaseIcons';
import { EmailTestIcons } from '../modules/root/EmailTestIcons';
import { SessionsIcons } from '../modules/root/SessionsIcons';
import { RightsIcons } from '../modules/rights/RightsIcons';
import { UsersIcons } from '../modules/users/UsersIcons';

interface InternalUrl {
    pathname: string;
    search: string;
}

class PageState {
    public path: string = $state('/');
    public search: string = $state('');
    /** The central definitions — reactive, so a changed badge or label shows in every menu at once. */
    public pages: Record<PageKey, PageDefinition> = $state({
        entities: {
            key: 'entities',
            resources: ['person', 'organization'],
            path: '/entities',
            label: PageTexts.pages.entities.label,
            buttonLabel: PageTexts.pages.entities.buttonLabel,
            description: PageTexts.pages.entities.description,
            icon: EntitiesIcons.Entities,
            load: () => import('../modules/entities/Entities.svelte'),
            sub: ['persons', 'organizations'],
        },
        persons: {
            key: 'persons',
            resources: ['person'],
            path: '/persons',
            label: PageTexts.pages.persons.label,
            buttonLabel: PageTexts.pages.persons.buttonLabel,
            description: PageTexts.pages.persons.description,
            icon: EntitiesIcons.Person,
            createIcon: EntitiesIcons.PersonCreate,
            load: () => import('../modules/entities/Persons.svelte'),
        },
        person: {
            key: 'person',
            resources: ['person'],
            path: '/persons/:id',
            label: PageTexts.pages.person.label,
            buttonLabel: PageTexts.pages.person.buttonLabel,
            description: PageTexts.pages.person.description,
            icon: EntitiesIcons.Person,
            load: () => import('../modules/entities/components/persons/PersonDetails.svelte'),
        },
        organizations: {
            key: 'organizations',
            resources: ['organization'],
            path: '/organizations',
            label: PageTexts.pages.organizations.label,
            buttonLabel: PageTexts.pages.organizations.buttonLabel,
            description: PageTexts.pages.organizations.description,
            icon: EntitiesIcons.Organization,
            createIcon: EntitiesIcons.OrganizationCreate,
            load: () => import('../modules/entities/Organizations.svelte'),
        },
        organization: {
            key: 'organization',
            resources: ['organization'],
            path: '/organizations/:id',
            label: PageTexts.pages.organization.label,
            buttonLabel: PageTexts.pages.organization.buttonLabel,
            description: PageTexts.pages.organization.description,
            icon: EntitiesIcons.Organization,
            load: () => import('../modules/entities/components/organizations/OrganizationDetails.svelte'),
        },
        admin: {
            key: 'admin',
            path: '/admin',
            label: PageTexts.pages.admin.label,
            buttonLabel: PageTexts.pages.admin.buttonLabel,
            description: PageTexts.pages.admin.description,
            icon: Icons.ShieldCheck,
            access: 'admin',
            load: () => import('../modules/admin/Admin.svelte'),
            sub: ['users', 'teams', 'tags', 'rights', 'positions', 'intranets'],
        },
        users: {
            key: 'users',
            path: '/users',
            label: PageTexts.pages.users.label,
            buttonLabel: PageTexts.pages.users.buttonLabel,
            description: PageTexts.pages.users.description,
            icon: UsersIcons.User,
            createIcon: UsersIcons.UserCreate,
            access: 'admin',
            load: () => import('../modules/users/Users.svelte'),
        },
        teams: {
            key: 'teams',
            path: '/teams',
            label: PageTexts.pages.teams.label,
            buttonLabel: PageTexts.pages.teams.buttonLabel,
            description: PageTexts.pages.teams.description,
            icon: UsersIcons.Team,
            access: 'admin',
            load: () => import('../modules/users/Teams.svelte'),
        },
        tags: {
            key: 'tags',
            path: '/tags',
            label: PageTexts.pages.tags.label,
            buttonLabel: PageTexts.pages.tags.buttonLabel,
            description: PageTexts.pages.tags.description,
            icon: RightsIcons.Tag,
            access: 'admin',
            load: () => import('../modules/rights/Tags.svelte'),
        },
        rights: {
            key: 'rights',
            path: '/rights',
            label: PageTexts.pages.rights.label,
            buttonLabel: PageTexts.pages.rights.buttonLabel,
            description: PageTexts.pages.rights.description,
            icon: RightsIcons.Right,
            access: 'admin',
            load: () => import('../modules/rights/Rights.svelte'),
        },
        tag: {
            key: 'tag',
            path: '/tags/:inx',
            label: PageTexts.pages.tag.label,
            buttonLabel: PageTexts.pages.tag.buttonLabel,
            description: PageTexts.pages.tag.description,
            icon: RightsIcons.Tag,
            access: 'admin',
            load: () => import('../modules/rights/components/TagDetails.svelte'),
        },
        right: {
            key: 'right',
            path: '/rights/:id',
            label: PageTexts.pages.right.label,
            buttonLabel: PageTexts.pages.right.buttonLabel,
            description: PageTexts.pages.right.description,
            icon: RightsIcons.Right,
            access: 'admin',
            load: () => import('../modules/rights/components/RightDetails.svelte'),
        },
        admin_team: {
            key: 'admin_team',
            path: '/teams/:id',
            label: PageTexts.pages.admin_team.label,
            buttonLabel: PageTexts.pages.admin_team.buttonLabel,
            description: PageTexts.pages.admin_team.description,
            icon: UsersIcons.Team,
            access: 'admin',
            load: () => import('../modules/users/components/teams/TeamDetails.svelte'),
        },
        admin_user: {
            key: 'admin_user',
            path: '/users/:id',
            label: PageTexts.pages.admin_user.label,
            buttonLabel: PageTexts.pages.admin_user.buttonLabel,
            description: PageTexts.pages.admin_user.description,
            icon: UsersIcons.User,
            access: 'admin',
            load: () => import('../modules/users/components/users/UserDetails.svelte'),
        },
        intranets: {
            key: 'intranets',
            path: '/intranets',
            label: PageTexts.pages.intranets.label,
            buttonLabel: PageTexts.pages.intranets.buttonLabel,
            description: PageTexts.pages.intranets.description,
            icon: IntranetIcons.Intranet,
            createIcon: IntranetIcons.IntranetCreate,
            access: 'admin',
            load: () => import('../modules/intranet/Intranets.svelte'),
        },
        admin_intranet: {
            key: 'admin_intranet',
            path: '/intranets/:id',
            label: PageTexts.pages.admin_intranet.label,
            buttonLabel: PageTexts.pages.admin_intranet.buttonLabel,
            description: PageTexts.pages.admin_intranet.description,
            icon: IntranetIcons.Intranet,
            access: 'admin',
            load: () => import('../modules/intranet/components/IntranetDetails.svelte'),
        },
        intranet: {
            key: 'intranet',
            resources: ['intranet'],
            path: '/intranet/:id',
            routes: ['/intranet/:id/:token', '/intranet/:id/:token/:point/:pointToken'],
            label: PageTexts.pages.intranet.label,
            buttonLabel: PageTexts.pages.intranet.buttonLabel,
            icon: IntranetIcons.Intranet,
            load: () => import('../modules/intranet/Intranet.svelte'),
        },
        positions: {
            key: 'positions',
            path: '/positions',
            label: PageTexts.pages.positions.label,
            buttonLabel: PageTexts.pages.positions.buttonLabel,
            description: PageTexts.pages.positions.description,
            icon: EntitiesIcons.Position,
            createIcon: EntitiesIcons.PositionCreate,
            access: 'admin',
            load: () => import('../modules/entities/Positions.svelte'),
        },
        position: {
            key: 'position',
            path: '/positions/:id',
            label: PageTexts.pages.position.label,
            buttonLabel: PageTexts.pages.position.buttonLabel,
            description: PageTexts.pages.position.description,
            icon: EntitiesIcons.Position,
            access: 'admin',
            load: () => import('../modules/entities/components/positions/PositionDetails.svelte'),
        },
        dashboard: {
            key: 'dashboard',
            path: '/',
            label: PageTexts.pages.dashboard.label,
            buttonLabel: PageTexts.pages.dashboard.buttonLabel,
            description: PageTexts.pages.dashboard.description,
            icon: Icons.LayoutDashboard,
            load: () => import('../modules/dashboard/Dashboard.svelte'),
            sub: ['ai_announcements'],
        },
        ai_announcements: {
            key: 'ai_announcements',
            resources: ['ai_announcement'],
            path: '/ai/announcements',
            label: PageTexts.pages.ai_announcements.label,
            buttonLabel: PageTexts.pages.ai_announcements.buttonLabel,
            description: PageTexts.pages.ai_announcements.description,
            icon: AiIcons.Announcement,
            createIcon: AiIcons.AnnouncementCreate,
            load: () => import('../modules/ai/AiAnnouncements.svelte'),
        },
        ai_announcement: {
            key: 'ai_announcement',
            resources: ['ai_announcement'],
            path: '/ai/announcements/:id',
            label: PageTexts.pages.ai_announcement.label,
            buttonLabel: PageTexts.pages.ai_announcement.buttonLabel,
            description: PageTexts.pages.ai_announcement.description,
            icon: AiIcons.Announcement,
            load: () => import('../modules/ai/components/AiAnnouncementDetails.svelte'),
        },
        favorites: {
            key: 'favorites',
            path: '/favorites',
            label: PageTexts.pages.favorites.label,
            buttonLabel: PageTexts.pages.favorites.buttonLabel,
            description: PageTexts.pages.favorites.description,
            icon: Icons.Star,
            access: 'login',
            load: () => import('../modules/favorites/Favorites.svelte'),
        },
        helpers: {
            key: 'helpers',
            path: '/helpers',
            label: PageTexts.pages.helpers.label,
            buttonLabel: PageTexts.pages.helpers.buttonLabel,
            description: PageTexts.pages.helpers.description,
            icon: HelpersIcons.Helpers,
            access: 'login',
            load: () => import('../modules/helpers/Helpers.svelte'),
            sub: ['plain_text'],
        },
        plain_text: {
            key: 'plain_text',
            path: '/helpers/plainText',
            label: PageTexts.pages.plain_text.label,
            buttonLabel: PageTexts.pages.plain_text.buttonLabel,
            description: PageTexts.pages.plain_text.description,
            icon: HelpersIcons.PlainText,
            access: 'login',
            load: () => import('../modules/helpers/PlainText.svelte'),
        },
        logout: {
            key: 'logout',
            path: '/logout',
            label: PageTexts.pages.logout.label,
            buttonLabel: PageTexts.pages.logout.buttonLabel,
            description: PageTexts.pages.logout.description,
            icon: Icons.LogOut,
            load: () => import('../modules/users/Logout.svelte'),
        },
        messages: {
            key: 'messages',
            path: '/messages',
            label: PageTexts.pages.messages.label,
            buttonLabel: PageTexts.pages.messages.buttonLabel,
            description: PageTexts.pages.messages.description,
            icon: Icons.Bell,
            load: () => import('../modules/messages/Messages.svelte'),
        },
        root: {
            key: 'root',
            path: '/root',
            label: PageTexts.pages.root.label,
            buttonLabel: PageTexts.pages.root.buttonLabel,
            description: PageTexts.pages.root.description,
            icon: Icons.ShieldAlert,
            access: 'root',
            load: () => import('../modules/root/Root.svelte'),
            sub: ['sessions', 'database', 'email_test'],
        },
        database: {
            key: 'database',
            path: '/database',
            label: PageTexts.pages.database.label,
            buttonLabel: PageTexts.pages.database.buttonLabel,
            description: PageTexts.pages.database.description,
            icon: DatabaseIcons.Database,
            access: 'root',
            load: () => import('../modules/root/Database.svelte'),
        },
        email_test: {
            key: 'email_test',
            path: '/emailTest',
            label: PageTexts.pages.email_test.label,
            buttonLabel: PageTexts.pages.email_test.buttonLabel,
            description: PageTexts.pages.email_test.description,
            icon: EmailTestIcons.EmailTest,
            access: 'root',
            load: () => import('../modules/root/EmailTest.svelte'),
        },
        sessions: {
            key: 'sessions',
            path: '/sessions',
            label: PageTexts.pages.sessions.label,
            buttonLabel: PageTexts.pages.sessions.buttonLabel,
            description: PageTexts.pages.sessions.description,
            icon: SessionsIcons.Session,
            access: 'root',
            load: () => import('../modules/root/Sessions.svelte'),
        },
        search: {
            key: 'search',
            path: '/search',
            label: PageTexts.pages.search.label,
            buttonLabel: PageTexts.pages.search.buttonLabel,
            description: PageTexts.pages.search.description,
            icon: Icons.Search,
            load: () => import('../modules/search/Search.svelte'),
        },
        microsoft: {
            key: 'microsoft',
            path: '/microsoft',
            label: PageTexts.pages.microsoft.label,
            description: PageTexts.pages.microsoft.description,
            icon: Icons.LogIn,
            load: () => import('../modules/users/MicrosoftCallback.svelte'),
        },
        user: {
            key: 'user',
            path: '/user',
            label: PageTexts.pages.user.label,
            buttonLabel: PageTexts.pages.user.buttonLabel,
            description: PageTexts.pages.user.description,
            icon: Icons.CircleUser,
            load: () => import('../modules/users/UserAccount.svelte'),
        },
    });
}

/**
 * The pages and which one is shown. Every page is defined once in PageState.pages (path, label, icon, load(),
 * badges); menus take their points from getPoint(key). The path follows the address bar through the History API;
 * init() catches every click on a same-origin `<a>` and calls set() instead of reloading. An unknown path becomes `/`.
 */
class PageServiceClass {
    private readonly HomeKey: PageKey = 'dashboard';
    private readonly TitleSuffix = PageTexts.TitleSuffix;
    private readonly State: PageState = new PageState();
    private IsInitialized = false;
    /** The page shown before the current one, for goBack(); null on the first page. */
    private PreviousTarget: string | null = null;
    /** The loading or loaded component of every page asked for so far — each one is fetched only once. */
    private readonly Components = new Map<PageKey, Promise<Component>>();

    public constructor() {
        this.readLocation();
    }

    /** The current page; an unknown path has already been turned into `/`. */
    public get page(): PageDefinition {
        return this.findPage(this.State.path) ?? this.State.pages[this.HomeKey];
    }

    /** The current path without query, `/` at least. */
    public get path(): string {
        return this.State.path;
    }

    /**
     * A new value mounts the page anew: every path, but all `routes` of a page count as its `path` with the same parameters,
     * so moving between them keeps it (the tree of an intranet stays while the point changes).
     */
    public get mountKey(): string {
        const Page = this.findPage(this.State.path);
        if (Page?.routes === undefined) {
            return this.State.path;
        }
        const Params = this.params;

        return Page.path.replace(/:(\w+)/g, (_: string, name: string): string => Params[name]);
    }

    /** A query or route parameter of the current address (`q` of `/search?q=…` or `id` of `/persons/123`). */
    public getParam(name: string): string | null {
        return this.params[name] ?? new URLSearchParams(this.State.search).get(name);
    }

    /** Route parameters extracted from the current path. */
    public get params(): Record<string, string> {
        const Page = this.findPage(this.State.path);
        if (Page === null) {
            return {};
        }

        return this.matchPage(Page, this.State.path) ?? {};
    }

    /** The icon of the page's "anlegen" button — its module's, `Icons.Plus` without one. */
    public getCreateIcon(key: PageKey): IconType {
        return this.State.pages[key].createIcon ?? Icons.Plus;
    }

    public getPath(key: PageKey): string {
        return this.State.pages[key].path;
    }

    /** The definition of a page — for its head (PageHead) and the like. */
    public getPage(key: PageKey): Readonly<PageDefinition> {
        return this.State.pages[key];
    }

    /**
     * The component of a page, fetched on the first call (its own chunk in the build), afterwards from memory. A failed
     * load (network gone, new build deployed) is forgotten, so the next call tries again.
     */
    public loadPage(page: PageDefinition): Promise<Component> {
        const Cached = this.Components.get(page.key);
        if (Cached !== undefined) {
            return Cached;
        }

        // -
        const Loading = page.load().then((module: { default: Component }): Component => module.default);
        Loading.catch((): void => {
            this.Components.delete(page.key);
        });
        this.Components.set(page.key, Loading);

        return Loading;
    }

    /** Whether the logged-in user may open the page (`access`, USER without one; `resources`) — menus, preloading and LyMain. */
    public canAccess(key: PageKey): boolean {
        return this.hasAccess(this.State.pages[key].access ?? 'user') && !this.isConcealed(key);
    }

    /** The page of a module the user has no right in: it does not exist for the user, not even as a refusal. */
    public isConcealed(key: PageKey): boolean {
        const Resources = this.State.pages[key].resources;

        return Resources !== undefined && !Resources.some((resource: string): boolean => UserService.canRead(resource));
    }

    /** The mark of what only admins or roots see — the icon of their area (`admin`, `root`). */
    public getAccessIcon(access: Exclude<PageAccess, 'login' | 'user'>): IconType {
        return this.State.pages[access].icon;
    }

    /** Whether the user has the level (ROOT ⊃ ADMIN ⊃ USER ⊃ a login alone). */
    public hasAccess(access: PageAccess): boolean {
        switch (access) {
            case 'login':
                return UserService.body.isLoggedIn;
            case 'user':
                return UserService.isUser;
            case 'admin':
                return UserService.isAdmin;
            case 'root':
                return UserService.isRoot;
        }
    }

    /** Fetches the page of an internal link in advance (pointer over it, keyboard focus) — no wait on the click. */
    public preload(href: string): void {
        const Url = this.toInternalUrl(href);
        const Page = Url === null ? null : this.findPage(Url.pathname);
        // - a page without the right is never fetched, its code stays on the server
        if (Page !== null && this.canAccess(Page.key)) {
            this.loadPage(Page).catch((): void => {});
        }
    }

    /** The definitions of a page's `sub` pages the user may open, in their order. */
    public listSubPages(key: PageKey): PageDefinition[] {
        return this.listAccessibleSubKeys(key).map((subKey: PageKey): PageDefinition => this.State.pages[subKey]);
    }

    /** The navigation point of a page — getPoint() plus its `sub` pages, recursively. */
    public getNavPoint(key: PageKey): NavPointData {
        const Sub = this.listAccessibleSubKeys(key);

        return {
            ...this.getPoint(key),
            ...(Sub.length > 0 ? { sub: Object.fromEntries(Sub.map((subKey: PageKey): [PageKey, NavPointData] => [subKey, this.getNavPoint(subKey)])) } : {}),
        };
    }

    /** The menu point of a page: label, icon, path, badges, and `active` while it is shown. */
    public getPoint(key: PageKey): DropdownPointData {
        const Page = this.State.pages[key];

        return {
            label: Page.label,
            icon: Page.icon,
            href: Page.path,
            active: Page.path === this.State.path || (Page.path !== '/' && this.State.path.startsWith(Page.path + '/')),
            alarm: Page.alarm,
            count: Page.count,
        };
    }

    private listAccessibleSubKeys(key: PageKey): PageKey[] {
        return (this.State.pages[key].sub ?? []).filter((subKey: PageKey): boolean => this.canAccess(subKey));
    }

    /** Leaves a page that does not exist for the user, like an unknown path. */
    public goHome(): void {
        this.set(this.State.pages[this.HomeKey].path);
    }

    /** Call once at start: follows back/forward, turns internal link clicks into set(), keeps the title. */
    public init(): void {
        if (this.IsInitialized) {
            return;
        }
        this.IsInitialized = true;

        // -
        window.addEventListener('popstate', (): void => {
            this.readLocation();
        });
        document.addEventListener('click', (event: MouseEvent): void => {
            this.handleClick(event);
        });
        document.addEventListener('mouseover', (event: MouseEvent): void => {
            this.handlePreload(event);
        });
        document.addEventListener('focusin', (event: FocusEvent): void => {
            this.handlePreload(event);
        });
        $effect.root(() => {
            $effect(() => {
                document.title = `${this.page.label} – ${this.TitleSuffix}`;
            });
        });
    }

    /** Back to the page shown before (a "cancel"), the home page without one. */
    public goBack(): void {
        this.set(this.PreviousTarget ?? this.State.pages[this.HomeKey].path);
    }

    /** Changes the badges of a page — every menu showing it follows. `undefined` removes one. */
    public setBadges(key: PageKey, badges: PointBadges): void {
        const Page = this.State.pages[key];
        if ('alarm' in badges) {
            Page.alarm = badges.alarm;
        }
        if ('count' in badges) {
            Page.count = badges.count;
        }
    }

    /** Shows the page of an internal URL and writes it into the history; an external one is opened normally. */
    public set(href: string): void {
        const Url = this.toInternalUrl(href);
        if (Url === null) {
            window.location.assign(href);
            return;
        }

        // -
        const Target = Url.pathname + Url.search;
        if (Target !== window.location.pathname + window.location.search) {
            window.history.pushState({}, '', Target);
        }
        this.applyUrl(Url);
        window.scrollTo(0, 0);
    }

    /** Like set(), but replaces the current history entry — for addresses that must not be visited again. */
    public replace(href: string): void {
        const Url = this.toInternalUrl(href);
        if (Url === null) {
            window.location.replace(href);
            return;
        }

        // -
        window.history.replaceState({}, '', Url.pathname + Url.search);
        this.applyUrl(Url);
        window.scrollTo(0, 0);
    }

    /** The page of a path; null for an unknown one. */
    protected findPage(path: string): PageDefinition | null {
        const Exact = Object.values(this.State.pages).find((page: PageDefinition): boolean => page.path === path);
        if (Exact !== undefined) {
            return Exact;
        }

        return Object.values(this.State.pages).find((page: PageDefinition): boolean => this.matchPage(page, path) !== null) ?? null;
    }

    /** The route parameters of a path by the addresses of a page; null when it is none of them. */
    private matchPage(page: PageDefinition, path: string): Record<string, string> | null {
        for (const Pattern of [page.path, ...(page.routes ?? [])]) {
            const Params = this.matchRoute(Pattern, path);
            if (Params !== null) {
                return Params;
            }
        }

        return null;
    }

    /** Matches a route pattern (e.g. `/persons/:id`) against a path (e.g. `/persons/123`). */
    private matchRoute(pattern: string, path: string): Record<string, string> | null {
        const PatternSegments = pattern.split('/').filter(Boolean);
        const PathSegments = path.split('/').filter(Boolean);
        if (PatternSegments.length !== PathSegments.length) {
            return null;
        }

        const Params: Record<string, string> = {};
        for (let i = 0; i < PatternSegments.length; i++) {
            if (PatternSegments[i].startsWith(':')) {
                const ParamName = PatternSegments[i].slice(1);
                Params[ParamName] = PathSegments[i];
            } else if (PatternSegments[i] !== PathSegments[i]) {
                return null;
            }
        }

        return Params;
    }

    /** Takes over path and query; an unknown path is replaced by the home page in the address bar too. */
    private applyUrl(url: InternalUrl): void {
        if (this.findPage(url.pathname) === null) {
            const Home = this.State.pages[this.HomeKey].path;
            window.history.replaceState({}, '', Home);
            this.State.path = Home;
            this.State.search = '';
            return;
        }

        // -
        if (url.pathname !== this.State.path) {
            this.PreviousTarget = this.State.path + this.State.search;
        }
        this.State.path = url.pathname;
        this.State.search = url.search;
    }

    /** A plain left click on a same-origin link in this tab; anything else stays with the browser. */
    private handleClick(event: MouseEvent): void {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        const Anchor = event.target instanceof Element ? event.target.closest('a') : null;
        if (Anchor === null || Anchor.hasAttribute('download') || (Anchor.target !== '' && Anchor.target !== '_self')) {
            return;
        }
        const Href = Anchor.getAttribute('href');
        if (Href === null || Href.startsWith('#') || this.toInternalUrl(Anchor.href) === null) {
            return;
        }

        // -
        event.preventDefault();
        this.set(Anchor.href);
    }

    /** Preloads the page of the same-origin link the pointer or the focus has just reached. */
    private handlePreload(event: Event): void {
        const Anchor = event.target instanceof Element ? event.target.closest('a') : null;
        if (Anchor !== null && Anchor.href !== '') {
            this.preload(Anchor.href);
        }
    }

    private readLocation(): void {
        this.applyUrl({ pathname: this.normalizePath(window.location.pathname), search: window.location.search });
    }

    /** `/user/` → `/user`; never empty. */
    private normalizePath(path: string): string {
        const Trimmed = path.replace(/\/+$/, '');

        return Trimmed === '' ? '/' : Trimmed;
    }

    /** Path (normalised) and query when the URL belongs to this frontend (same origin, not the API), else null. */
    private toInternalUrl(href: string): InternalUrl | null {
        try {
            const Url = new URL(href, window.location.origin);
            if (Url.origin !== window.location.origin || Url.pathname.startsWith('/api/')) {
                return null;
            }

            return { pathname: this.normalizePath(Url.pathname), search: Url.search };
        } catch {
            return null;
        }
    }
}

export const PageService = new PageServiceClass();
